import { initializeApp, getApps, getApp } from 'firebase/app';
import {
  getMessaging,
  getToken,
  isSupported,
  onMessage,
} from 'firebase/messaging';

import { CONFIG } from 'src/config-global';

const cfg = CONFIG.firebase;

export function isWebPushConfigured() {
  return !!(
    cfg.apiKey &&
    cfg.projectId &&
    cfg.messagingSenderId &&
    cfg.appId &&
    cfg.vapidKey
  );
}

let appPromise = null;
function getFirebaseApp() {
  if (!appPromise) {
    appPromise = Promise.resolve().then(() =>
      getApps().length > 0
        ? getApp()
        : initializeApp({
            apiKey: cfg.apiKey,
            authDomain: cfg.authDomain,
            projectId: cfg.projectId,
            storageBucket: cfg.storageBucket,
            messagingSenderId: cfg.messagingSenderId,
            appId: cfg.appId,
          }),
    );
  }
  return appPromise;
}

function buildSwUrl() {
  // Pass the public Firebase web config to the service worker via the script
  // URL — service workers cannot read import.meta.env. Storage bucket and auth
  // domain are forwarded for completeness; only apiKey/projectId/sender/appId
  // are strictly required at SW-init time.
  const params = new URLSearchParams({
    apiKey: cfg.apiKey,
    authDomain: cfg.authDomain,
    projectId: cfg.projectId,
    storageBucket: cfg.storageBucket,
    messagingSenderId: cfg.messagingSenderId,
    appId: cfg.appId,
  });
  return `/firebase-messaging-sw.js?${params.toString()}`;
}

let registrationPromise = null;
async function getOrRegisterServiceWorker() {
  if (!('serviceWorker' in navigator)) return null;
  if (!registrationPromise) {
    registrationPromise = navigator.serviceWorker.register(buildSwUrl(), {
      scope: '/',
    });
  }
  return registrationPromise;
}

/**
 * Resolve an FCM token for the current browser. Returns null when the
 * browser does not support web push, when permission has not been granted,
 * or when Firebase config is incomplete.
 */
export async function getWebPushToken() {
  if (!isWebPushConfigured()) return null;
  if (typeof window === 'undefined') return null;
  if (!(await isSupported())) return null;
  if (Notification.permission !== 'granted') return null;

  const app = await getFirebaseApp();
  const messaging = getMessaging(app);
  const swReg = await getOrRegisterServiceWorker();
  if (!swReg) return null;

  return getToken(messaging, {
    vapidKey: cfg.vapidKey,
    serviceWorkerRegistration: swReg,
  });
}

/**
 * Subscribe to foreground push messages. Returns an unsubscribe function.
 */
export async function onForegroundPush(handler) {
  if (!isWebPushConfigured()) return () => {};
  if (typeof window === 'undefined') return () => {};
  if (!(await isSupported())) return () => {};

  const app = await getFirebaseApp();
  const messaging = getMessaging(app);
  return onMessage(messaging, handler);
}

/**
 * Request notification permission. Returns the resulting permission string
 * ('granted', 'denied', or 'default').
 */
export async function requestWebPushPermission() {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return 'denied';
  }
  if (Notification.permission !== 'default') return Notification.permission;
  return Notification.requestPermission();
}
